import type { FunctionComponent } from 'react';

import { Anchor, Box, Card, Center, Grid, LoadingOverlay, Stack, Text, Title } from '@mantine/core';

import { type ThirdPartyMenu } from 'api/integrations/useGetMenuThirdParty';

import { RenderHours } from './RenderHours';

export type { ThirdPartyMenu };

type ThirdPartyMenusViewProps = {
  isLoading: boolean;
  hasError: boolean;
  menus: ThirdPartyMenu[];
  activeMenuHref: string;
  isMobile?: boolean;
};

/**
 * Pure presentation surface for the third-party synced menus block — accepts
 * every state (loading / error / empty / ready) as data so it can be driven
 * straight from Storybook. The container in `ThirdPartyMenus.tsx` wires the
 * API hook and `HeadquarterContext` into these props.
 */
export const ThirdPartyMenusView: FunctionComponent<ThirdPartyMenusViewProps> = ({
  isLoading,
  hasError,
  menus,
  activeMenuHref,
  isMobile = false,
}) => {
  if (isLoading) {
    return (
      <Card>
        <Box pos="relative" mih={80}>
          <LoadingOverlay visible zIndex={1} />
        </Box>
      </Card>
    );
  }

  if (hasError) {
    return (
      <Card>
        <Center>
          <Text mt="xl" mb="xl">
            There was an issue retrieving your menus. Please try again.
          </Text>
        </Center>
      </Card>
    );
  }

  if (menus.length === 0) {
    return (
      <Card>
        <Center>
          <Text mt="xl" mb="xl">
            No Menus Available
          </Text>
        </Center>
      </Card>
    );
  }

  return (
    <>
      <Title order={6} mb="xs2" mt="xl">
        Menus
      </Title>
      <Stack>
        <Text variant="sublabel-sm">
          View what your diner can see now:&nbsp;
          <Anchor target="_blank" href={activeMenuHref}>
            Active Menu
          </Anchor>
        </Text>

        <Grid justify="flex-start" gutter="md">
          {menus.map(menu => (
            <Grid.Col span={isMobile ? 12 : 4} key={menu.x_menu_id}>
              <RenderHours
                title={menu.name}
                displayHours={menu.display_hours}
                xMenuId={menu.x_menu_id}
                menuType={menu.menu_type}
              />
            </Grid.Col>
          ))}
        </Grid>
      </Stack>
    </>
  );
};
